<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { NButton, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import CvVersionList from './CvVersionList.vue'
import CvDocument from './CvDocument.vue'
import CvDocumentEditor from './CvDocumentEditor.vue'
import CvPreview from './CvPreview.vue'
import CvPublishPanel from './CvPublishPanel.vue'
import CvGenerationPanel from './CvGenerationPanel.vue'
import CvHeaderActions from './CvHeaderActions.vue'
import CvProfileForm from './CvProfileForm.vue'
import { useCvProfileStore } from '../stores/cv_profile'
import { useCvVersionsStore, type CvDocument as CvDocumentData } from '../stores/cv_versions'
import { useCvGenerationRunsStore } from '../stores/cv_generation_runs'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only Live CV page, three columns on wide screens: the contact block
// and the selected draft's section editor on the left, the selected version
// drawn as a light serif page in the centre (live with unsaved edits), and
// the versions list plus the publish switch on the right. Generation is a
// queued job watched over ActionCable; a draft is hand-corrected, approved
// from the header, and published.
const store = useCvProfileStore()
const versions = useCvVersionsStore()
const runs = useCvGenerationRunsStore()
const message = useMessage()
useDocumentTitle('Live CV')

onMounted(async () => {
  runs.subscribe()
  try {
    await Promise.all([store.fetchProfile(), versions.fetchVersions(), runs.fetchRuns()])
  } catch {
    // Each store's loadError surfaces in the template; nothing to do here.
  }
})

// The editor works on a copy of the selected draft so an unsaved edit never
// leaks into the list; selecting another version discards it.
const editing = ref<CvDocumentData | null>(null)
const savingDraft = ref(false)
const approving = ref(false)

watch(
  () => versions.selected,
  (selected) => {
    // JSON round-trip: the document is plain JSON, and structuredClone rejects a reactive proxy.
    editing.value = selected?.status === 'draft' ? JSON.parse(JSON.stringify(selected.data)) : null
  },
  { immediate: true },
)

const approveVersionId = computed(() => (versions.selected?.status === 'draft' ? versions.selected.id : null))
const liveVersion = computed(() => versions.versions.find((v) => v.status === 'approved') ?? null)
const hasApproved = computed(() => liveVersion.value !== null)
const contactLine = computed(() =>
  [store.profile.location, store.profile.email].filter((part): part is string => Boolean(part)).join(' · '),
)

async function saveDraft(): Promise<void> {
  if (!versions.selected || !editing.value) return
  savingDraft.value = true
  try {
    await versions.updateDraft(versions.selected.id, editing.value)
    message.success('Draft saved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save the draft')
  } finally {
    savingDraft.value = false
  }
}

async function approve(): Promise<void> {
  if (!versions.selected) return
  approving.value = true
  try {
    await versions.approve(versions.selected.id)
    message.success('Version approved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to approve')
  } finally {
    approving.value = false
  }
}

const publishing = ref(false)

async function setPublished(published: boolean): Promise<void> {
  publishing.value = true
  try {
    await store.setPublished(published)
    message.success(published ? 'Public page is on' : 'Public page is off')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to change publishing')
  } finally {
    publishing.value = false
  }
}

async function generate(): Promise<void> {
  const inFlight = runs.active?.id
  try {
    const run = await runs.generate()
    message.success(run.id === inFlight ? 'A draft is already generating' : 'Draft queued')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to queue a draft')
  }
}

const LABEL_CLASS = 'm-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint'
const CARD_CLASS = 'flex flex-col gap-2 rounded-2xl border border-border-subtle bg-surface-raised p-3.5'
</script>

<template>
  <RouteShell>
    <div class="mx-auto flex max-w-[1400px] flex-col gap-5 pb-6">
      <header class="flex flex-wrap items-end gap-3">
        <div class="min-w-[240px] flex-1">
          <h1 class="page-title">Live CV</h1>
          <p class="m-0 mt-1.5 text-ink-muted">Drafted from the brain, approved by you, published as a public page.</p>
        </div>
        <CvHeaderActions
          :generating="runs.active !== null"
          :triggering="runs.triggering"
          :approve-version-id="approveVersionId"
          :approving="approving"
          @generate="generate"
          @approve="approve"
        />
      </header>

      <div class="flex flex-wrap items-start gap-6">
        <section aria-label="Editor" class="flex min-w-0 max-w-[420px] flex-[1_1_340px] flex-col gap-3.5">
          <CvProfileForm />
          <template v-if="editing">
            <p :class="LABEL_CLASS" class="pt-1">Sections · v{{ versions.selected?.id }} draft</p>
            <CvDocumentEditor v-model="editing" />
            <NButton class="self-start" :loading="savingDraft" data-testid="cv-save-draft" @click="saveDraft">
              Save draft
            </NButton>
          </template>
          <p v-else-if="versions.selected" class="m-0 text-[13px] text-ink-faint" data-testid="cv-readonly-note">
            v{{ versions.selected.id }} is {{ versions.selected.status }} and read-only. Generate a new version to
            change it.
          </p>
        </section>

        <section aria-label="Preview" class="flex min-w-0 flex-[999_1_420px] flex-col gap-3">
          <div v-if="versions.selected" class="flex flex-col gap-3" data-testid="cv-selected">
            <CvPreview :version-id="versions.selected.id" :is-draft="versions.selected.status === 'draft'" />
            <CvDocument
              :document="editing ?? versions.selected.data"
              :name="store.profile.name"
              :contact="contactLine"
            />
            <div class="text-xs text-ink-muted" data-testid="cv-selected-citations">
              <span class="text-ink-faint">Grounded on:</span>
              <span v-for="(c, i) in versions.selected.citations" :key="c.path"
                ><span v-if="i > 0">, </span>{{ c.title }}</span
              >
            </div>
          </div>
          <div
            v-else
            class="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-border-control p-6 text-center text-ink-muted"
          >
            Select a version to see it as a page here.
          </div>
        </section>

        <aside
          aria-label="Versions and publishing"
          class="flex min-w-0 max-w-full flex-[1_1_240px] flex-col gap-3.5 lg:max-w-[300px]"
        >
          <section :class="CARD_CLASS" aria-labelledby="cv-versions-heading">
            <h2 id="cv-versions-heading" class="m-0 text-sm font-semibold text-ink">Versions</h2>
            <CvGenerationPanel :active="runs.active" :failed="runs.failed" />
            <div v-if="versions.loadError" class="text-sm text-status-alert" data-testid="cv-versions-load-error">
              Failed to load CV versions: {{ versions.loadError }}
            </div>
            <p
              v-else-if="versions.loaded && versions.versions.length === 0"
              class="m-0 text-[13px] text-ink-muted"
              data-testid="cv-versions-empty"
            >
              No versions yet. Generate the first draft once the contact block is saved; it takes about a minute.
            </p>
            <CvVersionList
              v-else
              :versions="versions.versions"
              :selected-id="versions.selectedId"
              @select="versions.select"
            />
          </section>
          <CvPublishPanel
            v-if="store.loaded && !store.loadError"
            :published="store.profile.published"
            :has-approved="hasApproved"
            :live-version="liveVersion"
            :saving="publishing"
            @toggle="setPublished"
          />
        </aside>
      </div>
    </div>
  </RouteShell>
</template>
